import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import {
  getAnalytics,
  isSupported,
  setAnalyticsCollectionEnabled,
  type Analytics,
} from 'firebase/analytics';

// Firebase Web config is public by design (same posture as a publishable
// Stripe key): API key restrictions are enforced server-side by Firebase
// per HTTP referrer / bundle id. Safe to ship in the client bundle.
const firebaseConfig = {
  apiKey: 'AIzaSyCAbhsIvpy__tTwpfHSLoaRWCYGP5mon38',
  authDomain: 'getfacade-android.firebaseapp.com',
  projectId: 'getfacade-android',
  storageBucket: 'getfacade-android.firebasestorage.app',
  messagingSenderId: '274221485590',
  appId: '1:274221485590:web:12e0022cd78c8532166f37',
  measurementId: 'G-VJS7EJG2TE',
} as const;

let firebaseApp: FirebaseApp | null = null;
let analyticsInstance: Analytics | null = null;

/**
 * Initialize Firebase + Google Analytics. Client-only.
 *
 * Only enabled when `NEXT_PUBLIC_APP_ENV === 'production'` — the same signal
 * `BuildIndicator` uses to mark non-prod builds. Gating on `NODE_ENV` alone
 * would also fire on `hybrid` (local dev server + prod API) and any staging
 * deploy, polluting the GA property. To force analytics on locally for
 * instrumentation testing, set `NEXT_PUBLIC_FIREBASE_ANALYTICS=on`.
 */
export async function initFirebaseAnalytics(): Promise<Analytics | null> {
  if (typeof window === 'undefined') return null;
  if (analyticsInstance) return analyticsInstance;

  const forceOn = process.env.NEXT_PUBLIC_FIREBASE_ANALYTICS === 'on';
  if (process.env.NEXT_PUBLIC_APP_ENV !== 'production' && !forceOn) return null;

  // `isSupported()` weeds out unsupported environments (SSR-prerender, some
  // privacy browsers, in-app webviews) before getAnalytics throws.
  const supported = await isSupported().catch(() => false);
  if (!supported) return null;

  firebaseApp = getApps()[0] ?? initializeApp(firebaseConfig);
  analyticsInstance = getAnalytics(firebaseApp);
  return analyticsInstance;
}

/**
 * Enable/disable analytics collection for the current session. Used to keep
 * internal QA/team (`is_test`) traffic out of the GA property — when disabled,
 * events are not sent to Google at all (mirrors the native
 * `setAnalyticsCollectionEnabled` opt-out on Android/Apple). No-op outside the
 * environments where analytics initializes (non-prod, SSR, unsupported).
 */
export async function setAnalyticsCollection(enabled: boolean): Promise<void> {
  const analytics = await initFirebaseAnalytics();
  if (analytics) setAnalyticsCollectionEnabled(analytics, enabled);
}
